import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Leaf,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Calendar as CalendarIcon,
  MapPin,
  Users,
} from "lucide-react";

const EMPTY = { name: "", date: "", time: "", location: "", description: "" };

export default function EventsAdmin() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const e = await api.get("/events");
      setEvents(e.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Load failed");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (eid) => {
    try {
      await api.delete(`/events/${eid}`);
      toast.success("Event removed");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="border-b border-[#e6e2d6] bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-xl text-stone-900">Bailey Family Reunion</span>
            <span className="label-eyebrow text-stone-400 ml-2">Events</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
              data-testid="events-back-link"
            >
              <ArrowLeft className="h-4 w-4" /> Families
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">The gathering</div>
            <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
              Reunion Events
            </h1>
            <p className="text-stone-600 mt-3 max-w-xl">
              Add every event — a picnic, dinner, church service. Families will
              see these on their private card.
            </p>
          </div>
          <Button
            className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
            onClick={() => setCreating(true)}
            data-testid="add-event-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add event
          </Button>
        </div>

        {events.length === 0 ? (
          <div className="bg-white border border-[#e6e2d6] rounded-2xl p-14 text-center text-stone-500">
            No events yet. Add your first event above.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {events.map((e) => {
              return (
                <div
                  key={e._id}
                  className="bg-white border border-[#e6e2d6] rounded-2xl p-6 card-lift"
                  data-testid={`event-card-${e._id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="label-eyebrow mb-1 flex items-center gap-2">
                        <CalendarIcon className="h-3 w-3" /> {e.date || "TBD"}
                        {e.time ? ` · ${e.time}` : ""}
                      </div>
                      <div className="font-serif text-2xl text-stone-900">
                        {e.name}
                      </div>
                      {e.location && (
                        <div className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" /> {e.location}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(e)}
                        data-testid={`edit-event-${e._id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmDelete(e)}
                        className="text-stone-500 hover:text-red-600"
                        data-testid={`delete-event-${e._id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {e.description && (
                    <p className="text-sm text-stone-600 mt-2">{e.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <EventEditor
        open={creating || !!editing}
        onOpenChange={(v) => {
          if (!v) {
            setCreating(false);
            setEditing(null);
          }
        }}
        initial={editing || EMPTY}
        isEdit={!!editing}
        onSave={async (payload) => {
          if (editing) {
            await api.put(`/events/${editing._id}`, payload);
            toast.success("Event updated");
          } else {
            await api.post("/events", payload);
            toast.success("Event added");
          }
          setEditing(null);
          setCreating(false);
          load();
        }}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Remove this event?</DialogTitle>
          </DialogHeader>
          <p className="text-stone-600">
            <b>{confirmDelete?.name}</b> will be removed.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              onClick={() => remove(confirmDelete._id)}
              data-testid="confirm-delete-event-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventEditor({ open, onOpenChange, initial, isEdit, onSave }) {
  const [value, setValue] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setValue({ ...EMPTY, ...initial });
  }, [initial, open]);

  const set = (patch) => setValue((v) => ({ ...v, ...patch }));
  const submit = async () => {
    if (!value.name.trim()) {
      toast.error("Event name is required");
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {isEdit ? "Edit event" : "Add event"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="label-eyebrow">Name</Label>
            <Input
              value={value.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Family Picnic"
              className="mt-2 bg-stone-50 border-stone-200"
              data-testid="event-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Date</Label>
              <Input
                type="date"
                value={value.date || ""}
                onChange={(e) => set({ date: e.target.value })}
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Time</Label>
              <Input
                type="time"
                value={value.time || ""}
                onChange={(e) => set({ time: e.target.value })}
                className="mt-2 bg-stone-50 border-stone-200"
                data-testid="event-time-input"
              />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Location</Label>
            <Input
              value={value.location || ""}
              onChange={(e) => set({ location: e.target.value })}
              className="mt-2 bg-stone-50 border-stone-200"
              placeholder="Riverside Park pavilion"
              data-testid="event-location-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Description</Label>
            <Textarea
              value={value.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              className="mt-2 bg-stone-50 border-stone-200"
              data-testid="event-description-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
            data-testid="event-save-btn"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
